<template>
  <div style="padding: 20px">
    <!-- ==================== 总览统计 ==================== -->
    <div class="stat-bar">
      <div class="stat-card stat-pending">
        <div class="stat-num">{{ stat.pending || 0 }}</div>
        <div class="stat-label">进行中（待签署 / 签署中）</div>
      </div>
      <div class="stat-card stat-done">
        <div class="stat-num">{{ stat.done || 0 }}</div>
        <div class="stat-label">已完成</div>
      </div>
      <div class="stat-card stat-failed">
        <div class="stat-num">{{ stat.failed || 0 }}</div>
        <div class="stat-label">发起失败（可重试）</div>
      </div>
      <div class="stat-card stat-rejected">
        <div class="stat-num">{{ stat.rejected || 0 }}</div>
        <div class="stat-label">已拒签（可重新发起）</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ stat.total || 0 }}</div>
        <div class="stat-label">全部合同</div>
      </div>
      <div class="stat-tools">
        <span class="auto-tip">自动刷新</span>
        <el-switch v-model="autoRefresh" @change="onAutoRefreshChange"></el-switch>
        <span class="auto-tip">{{ autoRefresh ? '（30 秒）' : '' }}</span>
      </div>
    </div>

    <!-- ==================== 筛选栏 ==================== -->
    <div class="filter-bar">
      <el-select
          v-model="query.templateCode"
          placeholder="合同模板"
          clearable
          style="width: 200px; margin-right: 10px">
        <el-option
            v-for="t in templateOptions"
            :key="t.templateCode"
            :label="t.ready === false ? (t.templateName + '（模板未就绪）') : t.templateName"
            :value="t.templateCode">
        </el-option>
      </el-select>

      <el-select
          v-model="query.state"
          placeholder="签署状态"
          clearable
          style="width: 150px; margin-right: 10px">
        <el-option
            v-for="s in stateOptions"
            :key="s.value"
            :label="s.label"
            :value="s.value">
        </el-option>
      </el-select>

      <el-input
          v-model="query.partyAName"
          placeholder="交易对方名称"
          clearable
          style="width: 180px; margin-right: 10px"
          @keyup.enter="handleSearch">
      </el-input>

      <el-date-picker
          v-model="query.dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="发起开始"
          end-placeholder="发起结束"
          style="width: 260px; margin-right: 10px">
      </el-date-picker>

      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleReset">重置</el-button>
      <el-button type="success" @click="openSendDialog">+ 发起签署</el-button>
      <el-button @click="goTemplateManage">合同模板管理</el-button>
    </div>

    <!-- ==================== 签署列表 ==================== -->
    <el-table
        v-loading="tableLoading"
        :data="tableData"
        border
        :row-style="{ height: '20px' }"
        :cell-style="{ padding: '12px' }"
        class="table"
        header-cell-class-name="table-header">
      <el-table-column prop="contractNo" label="合同编号" width="180" show-overflow-tooltip></el-table-column>
      <el-table-column prop="templateName" label="合同模板" width="180" show-overflow-tooltip></el-table-column>
      <el-table-column prop="partyAName" label="交易对方" min-width="180" show-overflow-tooltip></el-table-column>
      <el-table-column prop="partyARep" label="对方代表人" width="110" show-overflow-tooltip></el-table-column>
      <el-table-column label="我方签署" width="135" align="center">
        <template #default="scope">
          <el-tag size="small" type="warning" effect="plain" style="margin-right:3px">
            {{ scope.row.ourRole === 'partyA' ? '甲方侧' : '乙方侧' }}
          </el-tag>
          <el-tag :type="signStatusTag(scope.row.partyBSignStatus)" size="small" effect="light">
            {{ signStatusText(scope.row.partyBSignStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="对方签署" width="135" align="center">
        <template #default="scope">
          <el-tag size="small" type="info" effect="plain" style="margin-right:3px">
            {{ scope.row.ourRole === 'partyA' ? '乙方侧' : '甲方侧' }}
          </el-tag>
          <el-tag :type="signStatusTag(scope.row.partyASignStatus)" size="small" effect="light">
            {{ signStatusText(scope.row.partyASignStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="110" align="center">
        <template #default="scope">
          <el-tag :type="stateTag(scope.row.state)" size="small" effect="dark">
            {{ scope.row.stateName }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="发起时间" width="160" show-overflow-tooltip>
        <template #default="scope">{{ formatTime(scope.row.sendTime || scope.row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="完成时间" width="160" show-overflow-tooltip>
        <template #default="scope">{{ formatTime(scope.row.finishTime) }}</template>
      </el-table-column>
      <el-table-column label="有效期至" width="160" show-overflow-tooltip>
        <template #default="scope">
          <span v-if="scope.row.validEnd">{{ formatTime(scope.row.validEnd) }}</span>
          <span v-else class="mini">长期有效</span>
        </template>
      </el-table-column>
      <el-table-column prop="createBy" label="发起人" width="100" show-overflow-tooltip></el-table-column>
      <el-table-column label="操作" width="330" fixed="right" align="center">
        <template #default="scope">
          <el-button size="small" type="text" @click="openDetail(scope.row)">详情</el-button>
          <el-button
              v-if="scope.row.state === 7"
              size="small"
              type="text"
              class="op-strong"
              @click="doRetry(scope.row)">重新发起</el-button>
          <!-- 对方未实名：这是「链接发了但对方签不了」的唯一可见信号，必须给处置入口 -->
          <el-button
              v-if="scope.row.partyAVerifyStatus === 'unverified'"
              size="small"
              type="text"
              class="op-strong"
              @click="copyVerifyUrl(scope.row)">对方未实名·取实名链接</el-button>
          <el-button
              v-if="canSign(scope.row) && scope.row.state !== 7"
              size="small"
              type="text"
              @click="copySignUrl(scope.row)">签署链接</el-button>
          <el-button
              v-if="scope.row.state !== 7"
              size="small"
              type="text"
              @click="doRefresh(scope.row)">刷新</el-button>
          <el-dropdown style="margin-left: 8px" @command="cmd => handleMore(cmd, scope.row)">
            <el-button size="small" type="text">更多<i class="el-icon-arrow-down el-icon--right"></i></el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="preview" v-if="scope.row.previewUrl">预览合同</el-dropdown-item>
                <el-dropdown-item command="copyLink" v-if="canSign(scope.row)">复制链接+转发话术</el-dropdown-item>
                <el-dropdown-item command="regen" v-if="canSign(scope.row)">强制重新生成链接</el-dropdown-item>
                <el-dropdown-item command="resend" v-if="scope.row.state === 2 || scope.row.state === 3">重发 / 催签</el-dropdown-item>
                <el-dropdown-item command="download" v-if="scope.row.state === 4">下载已签PDF</el-dropdown-item>
                <el-dropdown-item command="verify" v-if="scope.row.state === 4">合同验签（签章真伪）</el-dropdown-item>
                <el-dropdown-item command="urge" v-if="scope.row.state === 2 || scope.row.state === 3">仅催签</el-dropdown-item>
                <el-dropdown-item command="beforeAuth" v-if="scope.row.state === 2 || scope.row.state === 3 || scope.row.state === 4">取意愿认证链接</el-dropdown-item>
                <el-dropdown-item command="recheck">强制重算状态（权威查询）</el-dropdown-item>
                <el-dropdown-item command="restart" v-if="canRestart(scope.row)">重新发起（新建合同）</el-dropdown-item>
                <el-dropdown-item command="cancel" divided v-if="scope.row.state !== 4 && scope.row.state !== 5 && scope.row.state !== 7">撤销合同</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <!-- ==================== 分页 ==================== -->
    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[10, 20, 50, 100]"
          :current-page="query.pageNo"
          :page-size="query.pageSize"
          :total="query.total"
          @current-change="handlePageChange"
          @size-change="handleSizeChange">
      </el-pagination>
    </div>

    <!-- ==================== 发起签署弹窗 ==================== -->
    <el-dialog title="发起合同签署" v-model="sendDialog" width="760px" :close-on-click-modal="false">
      <el-form :model="sendForm" label-width="120px" size="default">
        <el-divider content-position="left">① 选择合同模板</el-divider>
        <el-form-item label="合同模板" required>
          <el-select
              v-model="sendForm.templateCode"
              placeholder="请选择要发送的合同模板"
              style="width: 100%"
              @change="onTemplateChange">
            <el-option
                v-for="t in templateOptions"
                :key="t.templateCode"
                :label="t.ready === false ? (t.templateName + '（模板未就绪，不可发起）') : t.templateName"
                :value="t.templateCode"
                :disabled="t.ready === false">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="签署标题">
          <el-input v-model="sendForm.subject" placeholder="不填则自动使用「模板名（交易对方名）」"></el-input>
        </el-form-item>

        <!-- 模板未就绪：在填任何内容之前就告明，避免白填一遍 -->
        <el-alert
            v-if="sendForm.templateCode && currentTemplate.ready === false"
            type="warning"
            :closable="false"
            show-icon
            style="margin-bottom: 16px"
            title="该合同模板还不能发起"
            :description="currentTemplate.unreadyReason">
        </el-alert>

        <!-- 无可用模板：给出明确下一步 -->
        <el-alert
            v-if="templateOptions.length > 0 && readyTemplates.length === 0"
            type="error"
            :closable="false"
            show-icon
            style="margin-bottom: 16px"
            title="当前没有可直接发起的合同模板"
            description="模板「未就绪」通常是两种情况：① 还没配合同 PDF（老版法大大必须先上传做好的 PDF，请填 sign_contract_template.pdf_url 或在 yml 配 fdd.contract.pdfPath）；② 模板是「我方自动盖章」但还没配【我方电子印章ID】（fdd.contract.corpSignatureId，去法大大后台「印章管理」取）。配好后即可发起。">
        </el-alert>

        <template v-if="sendForm.templateCode">
          <!-- 【2026-09-14】方位说明：本页「甲方信息」= 法大大老版接口里的甲方主体 = 【交易对方】，
               与合同 PDF 上的甲/乙方栏位无关（栏位由模板「我方角色」决定，可对称互换） -->
          <el-alert
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 12px"
              :title="'本模板方位：' + ourRoleText + '（占合同 ' + mySideLabel + ' 栏）｜交易对方 = 合同 ' + cpSideLabel + ' 栏'">
            <div style="line-height: 1.9">
              <b>两家的公司名会这样落进合同正文：</b>
              <span v-for="s in bodySides" :key="s.side" style="margin-right: 14px; white-space: nowrap">
                合同<b>{{ s.side }}</b>栏 ← <b>{{ s.owner }}</b>
                <template v-if="s.bound">（{{ s.company }}）</template>
              </span>
            </div>
            <div style="margin-top: 6px">
              <b>签署要素：</b>
              我方（{{ mySideLabel }}栏）= <b>{{ myElementsText }}</b>；
              对方（{{ cpSideLabel }}栏）= <b>{{ cpElementsText }}</b>。
              合同有效期 <b>{{ currentTemplate.validDays === 0 ? '长期有效' : ((currentTemplate.validDays || 30) + ' 天') }}</b>。
            </div>
            <div style="margin-top: 4px">
              提示：本页 <b>② 区恒填【交易对方】</b>（法大大老版接口中的"甲方主体"），
              <b>④ 区恒填【我方·焱火】</b>；上面那两个「甲方栏 / 乙方栏」才是合同 PDF 上的实际位置，
              由模板的「我方角色」决定，可能互换。
            </div>
          </el-alert>
          <el-divider content-position="left">② {{ cpSideLabel }}信息 —— 交易对方（占合同 {{ cpSideLabel }} 栏）</el-divider>
          <el-form-item :label="cpSideLabel + '类型'">
            <el-radio-group v-model="sendForm.partyAType">
              <el-radio label="corp">企业</el-radio>
              <el-radio label="person">自然人</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="sendForm.partyAType === 'corp' ? cpSideLabel + '企业全称' : cpSideLabel + '姓名'" required>
            <el-input v-model="sendForm.partyAName" placeholder="与营业执照/身份证完全一致"></el-input>
          </el-form-item>
          <!-- 【2026-09-11 冗余清理】自然人本人签约，无代表人概念；企业时才显示。
               且授权代表人只是写进 PDF 的内容字段，person 时不填也自动取甲方姓名 -->
          <el-form-item v-if="sendForm.partyAType === 'corp'" :label="cpSideLabel + '授权代表人'">
            <el-input v-model="sendForm.partyARep" :placeholder="cpSideLabel + '合同负责人姓名'"></el-input>
          </el-form-item>
          <el-form-item :label="cpSideLabel + '联系电话'">
            <el-input v-model="sendForm.partyAPhone"
                      :placeholder="'建议填写：填了法大大自动向' + cpSideLabel + '下发签署短信'"
                      @blur="checkPartyAAccount"></el-input>
            <div class="field-tip">
              <!-- 【2026-09-11】填完手机号即问法大大：该号是否已有账号 / 是否已实名。
                   个人实名判定走 query_signature（get_auth_status 对个人恒为 0，不可用）。 -->
              <span v-if="accountCheck.loading" class="tip-info">
                ⏳ 正在向法大大核对该手机号的账号与实名情况…
              </span>
              <template v-else-if="accountCheck.msg">
                <el-tag v-if="accountCheck.realName === true" size="mini" type="success" style="margin-right:6px">
                  已实名
                </el-tag>
                <el-tag v-else-if="accountCheck.realName === false" size="mini" type="warning" style="margin-right:6px">
                  未实名
                </el-tag>
                <el-tag v-else-if="accountCheck.hasAccount" size="mini" type="info" style="margin-right:6px">
                  实名未知
                </el-tag>
                <span :class="accountCheck.ok ? 'tip-ok' : 'tip-warn'">
                  {{ accountCheck.ok ? "✓ " : "⚠ " }}{{ accountCheck.msg }}
                </span>
              </template>
              <span v-else-if="sendForm.partyAPhone" class="tip-ok">
                ✓ 发起后法大大将自动向该号码下发签署短信（内含签署链接），{{ cpSideLabel }}点开即可签
              </span>
              <span v-else class="tip-warn">
                ⚠ 未填手机号时法大大不会发短信，需要你手动把签署链接转发给{{ cpSideLabel }}
              </span>
            </div>
          </el-form-item>
          <!-- 【2026-09-12 修正】地址输入框：③ 只渲染手工字段，而地址域带 defaultFrom 属静默字段，
               两边都不显示 → 合同正文「地址」永远空白（真实合同 HT20260912191230534 实测）。
               【2026-09-14】标签与归属随模板「我方角色」联动：本栏恒填【交易对方】地址，
               但只有模板 PDF 里存在对应域时才会渲染（下方给出即时提示）。 -->
          <el-form-item :label="cpSideLabel + '地址'">
            <el-input v-model="sendForm.partyAAddress"
                      :placeholder="'选填：写入合同正文 ' + cpSideLabel + ' 栏「地址」，留空则该栏空白'"></el-input>
            <div class="field-tip" v-if="!cpAddressBound">
              <span class="tip-warn">
                ⚠ 当前模板 PDF 里没有「{{ cpSideLabel }}地址」对应的表单域，本栏填了<b>不会</b>渲染到合同上。
              </span>
            </div>
          </el-form-item>
          <el-form-item :label="cpSideLabel + '法大大客户号'">
            <el-input v-model="sendForm.partyACustomerId"
                      :placeholder="'选填：' + cpSideLabel + '已在法大大注册过时填写，填了直接用它出签署链接'"></el-input>
            <div class="field-tip">
              <span v-if="sendForm.partyACustomerId" class="tip-ok">
                ✓ 将直接使用该客户号生成签署链接（无需自动注册，不受 IP 白名单限制）
              </span>
              <span v-else class="tip-warn">
                ⚠ 留空则系统自动为{{ cpSideLabel }}注册法大大账号（需法大大后台已绑定服务器出口 IP 白名单）；若{{ cpSideLabel }}已是法大大用户，建议直接填写其客户号
              </span>
            </div>
          </el-form-item>

          <el-divider content-position="left">③ 表单内容（写入合同正文）</el-divider>
          <div v-if="manualFormFields.length > 0" class="field-tip" style="margin: -6px 0 12px">
            <span class="tip-warn">
              合同正文「甲方栏 / 乙方栏」的公司名称、联系电话、地址、签订日期，
              已按 ② / ④ 两区填写内容自动写入（我方角色不同时会自动对调栏位方向），无需重复填写；
              「服务开始日 / 服务结束日」需你确认（默认值是今天起 5 天，可直接改，结束日不得早于开始日）。
              发起后合同内容不可修改，发现填错只能重新发起新合同
            </span>
          </div>
          <div v-if="formFields.length === 0" class="tip-empty">
            该模板未配置表单字段，合同将按 PDF 原始内容发送。
          </div>
          <!-- 【2026-09-11 二次清理】凡能自动带出的字段（甲方名称/电话/地址、乙方名称/电话、
               代表人、签订日期、服务起止日）一律不再显示，静默写进合同正文；
               只保留真正需要人工确认的项，并按甲方/乙方分组间隔展示 -->
          <template v-for="g in manualGroups">
            <el-divider v-if="g.title" :key="'div-' + g.title" content-position="left">{{ g.title }}</el-divider>
            <el-form-item
                v-for="f in g.fields"
                :key="f.key"
                :label="f.label || f.key"
                :required="f.required === true">
              <el-input
                  v-if="f.type !== 'textarea'"
                  v-model="sendForm.formData[f.key]"
                  :placeholder="f.placeholder || ('请输入' + (f.label || f.key))">
              </el-input>
              <el-input
                  v-else
                  type="textarea"
                  :rows="2"
                  v-model="sendForm.formData[f.key]"
                  :placeholder="f.placeholder || ('请输入' + (f.label || f.key))">
              </el-input>
            </el-form-item>
          </template>

          <el-divider content-position="left">④ 我方（焱火）信息 —— 占合同 {{ mySideLabel }} 栏</el-divider>
          <el-form-item :label="'我方名称（写入合同 ' + mySideLabel + ' 栏）'">
            <el-input v-model="sendForm.partyBName" :placeholder="currentTemplate.partyBName || '默认取系统配置的我方公司'"></el-input>
          </el-form-item>
          <!-- 【2026-09-14】我方地址：仅当模板 PDF 的「我方所在栏」存在地址域时才有意义
               （our_role=partyA 时即原 txtComAAddress —— 取值来源随之翻转为我方地址）。
               历史模板（our_role=partyB）我方所在栏是乙方栏，一般没有地址域 ⇒ 本项自动隐藏。 -->
          <el-form-item v-if="myAddressBound" label="我方地址">
            <el-input v-model="sendForm.partyBAddress"
                      :placeholder="'选填：写入合同 ' + mySideLabel + ' 栏「地址」，留空则该栏空白'"></el-input>
            <div class="field-tip">
              <span class="tip-info">
                当前模板 PDF 的「{{ mySideLabel }}栏」有地址域，本栏填了会渲染上去。
              </span>
            </div>
          </el-form-item>
          <!-- 【2026-09-11 新增】我方授权代表人与联系电话：写进合同正文「授权代表人（负责人）」
               「联系电话」两栏，配一次长期复用（也可在 yml fdd.contract.partyBRep 设默认）。
               【2026-09-14】栏位名由「乙方」改为按模板角色动态显示（我方=甲方时落甲方栏）。 -->
          <el-form-item :label="'我方授权代表人（' + mySideLabel + '栏）'">
            <el-input v-model="sendForm.partyBRep" placeholder="我方经办人姓名"></el-input>
            <div class="field-tip">
              <span class="tip-warn">
                ⚠️ 若当前合同 PDF 里<b>没有「授权代表人」这个表单域</b>，这里填了不会渲染到合同上
                （要显示需模板制作人在 PDF 里加一个域，再补 field_schema 对应项，代码无需改动）。
                我方一侧目前以电子印章为准。
              </span>
            </div>
          </el-form-item>
          <el-form-item :label="'我方联系电话（' + mySideLabel + '栏）'">
            <el-input v-model="sendForm.partyBPhone" placeholder="我方联系电话（写入合同正文，留空则该栏空白）"></el-input>
          </el-form-item>
          <!-- 印章核对：直接问法大大要可用印章列表，避免 corpSignatureId 配了一个不存在的章却发现不了 -->
          <el-form-item label="我方电子印章">
            <div style="display:flex; align-items:center; gap:8px; width:100%">
              <span class="tip-ok" v-if="currentTemplate.corpSignatureId">
                使用印章ID：{{ currentTemplate.corpSignatureId }}
              </span>
              <span class="tip-warn" v-else>未配置印章ID，无法自动盖章</span>
              <el-button size="mini" :loading="signatureLoading" @click="loadCorpSignatures">
                从法大大核对可用印章
              </el-button>
            </div>
            <div class="field-tip" v-if="corpSignatures.length">
              <span class="tip-ok">
                法大大平台上可用印章 {{ corpSignatures.length }} 枚：
                {{ corpSignatures.map(s => s.signatureId).join("、") }}
                （法大大老版接口<b>不返回印章名称、也不支持通过接口命名</b>，
                新增印章请到「法大大控制台 → 印章管理」上传并命名/授权，再用这里核对编号）
              </span>
            </div>
          </el-form-item>
          <el-alert
              v-if="currentTemplate.partyBAutoSign === 2"
              type="success"
              :closable="false"
              show-icon
              title="我方为「盖章 + 授权负责人手写」模式：公章自动盖好，再由授权代表人手写签名（章与签字两要素齐备）">
            <div>
              发起时自动加盖我方电子公章，同时生成一条<b>我方授权代表人手写链接</b>（在签署记录里复制转发）；
              对方签署链接同时生成。几路签署互不阻塞，<b>公章 + 手写都完成</b>后我方才算签毕。
            </div>
            <div>使用印章ID：{{ currentTemplate.corpSignatureId || '（未配置，无法发起）' }}</div>
          </el-alert>
          <el-alert
              v-else-if="currentTemplate.partyBAutoSign === 1"
              type="success"
              :closable="false"
              show-icon
              title="我方为企业，将自动加盖电子印章（无需人工操作）">
            <div>
              落章方式：<b>坐标定位优先</b>（后端按模板 code 配了坐标就用坐标，精准落在「授权代表人（负责人）」行），
              未配置坐标时退回关键字定位：我方占合同 <b>{{ mySideLabel }}</b> 栏，用关键字
              「{{ myKeyword }}」。
              <span class="tip-warn">当前模板是否已配坐标，以发起后的《签署记录》里「签章坐标定位」日志与已签 PDF 实测落点为准。</span>
            </div>
            <div>使用印章ID：{{ currentTemplate.corpSignatureId || '（未配置，无法发起）' }}</div>
          </el-alert>
          <el-alert
              v-else
              type="warning"
              :closable="false"
              show-icon
              title="该模板为「我方授权负责人手写」模式（顺序签署：先我方手写，再发对方链接）">
            <div>
              发起后<b>不会</b>立即生成对方链接，而是先给出一条<b>我方授权代表人手写链接</b>，由我方授权代表人打开完成手写签名；
              我方签完回到列表点【刷新】，系统会自动生成对方签署链接并发短信。
            </div>
          </el-alert>

          <el-alert
              type="info"
              :closable="false"
              show-icon
              style="margin-bottom: 16px"
              :title="'对方（交易对手 · 落 ' + cpSideLabel + '栏）签署要素：' + cpElementsText">
            <div v-if="cpElementsText.indexOf('未配置') >= 0" class="tip-warn">
              模板未给对方配置任何签署要素，合同无法生效 —— 请到「合同模板管理」开启
              「对方企业盖章」或「对方授权负责人手写签字」后重试。
            </div>
            <div v-else>
              对方签署链接在发起后生成并下发短信；对方须先完成法大大实名认证才能签署。<br/>
              <span v-if="sendForm.partyAType === 'corp'">
                对方是企业：若走「授权负责人手写签字」，对方公司需在法大大完成<b>企业实名</b>后由负责人手写；
                若对方未授权电子章，请勿开启「对方企业盖章」（我方无法代对方盖章）。
              </span>
              <span v-else>对方是自然人：只能手写签字（无公章可盖）。</span>
            </div>
          </el-alert>

          <el-divider content-position="left">⑤ 图片表单域（按 PDF 识别结果动态渲染上传位）</el-divider>
          <div class="field-tip" style="margin: -6px 0 12px">
            <b>PDF 里有几个图片表单域，这里就有几个上传位</b>
            （身份证正/反、学历、学籍、银行卡、一寸免冠照等由模板识别自动判定，不再写死 4 格）。
            上传后自动传到 MinIO 并回填；<b>留空则合同对应位置不渲染</b>（不影响签署）。
          </div>
          <el-row :gutter="12">
            <el-col :span="12" v-for="f in currentImageFields" :key="f.name">
              <el-form-item :label="(f.label || f.name) + (f.required ? '（必传）' : '')">
                <el-upload :http-request="e => uploadSignImage(e, f.name)" :show-file-list="false"
                           :before-upload="beforeImg" accept="image/*">
                  <el-button :loading="!!uploading[f.name]" icon="Upload">上传</el-button>
                </el-upload>
                <img v-if="sendForm.images[f.name]" :src="sendForm.images[f.name]" class="attach-thumb"
                     @click="openUrl(sendForm.images[f.name])"/>
                <el-input v-model="sendForm.images[f.name]" size="small" placeholder="留空不渲染" style="margin-top:4px"/>
                <div class="field-tip">{{ f.name }}</div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-alert v-if="!currentImageFields.length" type="info" :closable="false" show-icon
                    title="当前模板未识别到图片表单域，无需上传图片">
            <div>如需按 PDF 出现上传位，请到「合同模板管理」页点该模板的「识别表单域」后保存。</div>
          </el-alert>
        </template>

        <el-form-item label="备注">
          <el-input type="textarea" :rows="2" v-model="sendForm.remark" placeholder="选填"></el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button :loading="prechecking" :disabled="!sendForm.templateCode" @click="doPrecheckSend">发起前预检</el-button>
        <el-button @click="sendDialog = false">取消</el-button>
        <el-button
            type="primary"
            :loading="sending"
            :disabled="!sendForm.templateCode || currentTemplate.ready === false"
            @click="doSend">发起签署并生成链接</el-button>
      </template>
    </el-dialog>

    <!-- ==================== 发起结果弹窗 ==================== -->
    <el-dialog title="签署链接已生成" v-model="resultDialog" width="700px">
      <template v-if="sendResult.waitingFirst || sendResult.waitingPartyB">
        <el-alert
            type="warning"
            :closable="false"
            show-icon
            :title="'当前为「我方先签」模式（我方＝' + resultMySide + '）：已创建合同，但还没发对方链接'"
            :description="'请先让我方授权代表人打开下方链接完成签署（手写签名 / 盖章）；我方签完后回到列表点【刷新】，系统会自动生成对方签署链接并发短信。'">
        </el-alert>
        <div class="result-block">
          <div class="result-row">
            <span class="result-label">合同编号</span>
            <span class="result-value">{{ sendResult.contractNo }}</span>
            <el-button size="small" type="text" @click="copyText(sendResult.contractNo)">复制</el-button>
          </div>
          <div class="result-row">
            <span class="result-label">我方签署链接</span>
            <span class="result-value link">{{ resultMySignUrl }}</span>
          </div>
          <div class="result-actions">
            <el-button type="primary" @click="copyText(resultMySignUrl)">复制我方签署链接</el-button>
            <el-button @click="openUrl(resultMySignUrl)">打开链接</el-button>
          </div>
        </div>
      </template>

      <template v-else>
      <!-- 【2026-09-12】未实名时后端返回的主链接就是【实名短链】（实名完自动跳签署页），
           这里必须提示清楚，避免运营误以为发错了 -->
      <el-alert
          v-if="sendResult.linkType === 'verify'"
          type="warning"
          :closable="false"
          show-icon
          title="交易对方尚未实名：下方链接是【实名短链】"
          description="把这条链接发给对方，他完成实名后会自动进入签署页签字。请勿另发签署链接（未实名时点开只会看到「尚未实名认证」提示）。">
      </el-alert>
      <el-alert
          v-if="sendResult.smsNotified"
          type="success"
          :closable="false"
          show-icon
          :title="'签署短信已下发至 ' + (sendResult.partyAPhone || '')"
          description="法大大已自动向交易对方手机号发送签署短信（内含签署链接）。你也可以复制下方链接，通过微信/邮件再发一份给对方。">
      </el-alert>
      <el-alert
          v-else
          type="warning"
          :closable="false"
          show-icon
          title="未填交易对方手机号，法大大不会自动发短信"
          description="请点击下方「复制链接+转发话术」，把已生成的话术和链接通过微信/短信发给对方。">
      </el-alert>

      <div class="result-block">
        <div class="result-row">
          <span class="result-label">合同编号</span>
          <span class="result-value">{{ sendResult.contractNo }}</span>
          <el-button size="small" type="text" @click="copyText(sendResult.contractNo)">复制</el-button>
        </div>
        <div class="result-row">
          <span class="result-label">{{ sendResult.linkType === 'verify' ? '实名短链（发这条）' : '交易对方签署链接' }}</span>
          <span class="result-value link">{{ sendResult.signUrl }}</span>
        </div>
        <div class="result-actions">
          <el-button type="primary" @click="copyText(sendResult.signUrl)">{{ sendResult.linkType === 'verify' ? '复制实名短链' : '复制签署链接' }}</el-button>
          <el-button type="success" @click="copyLinkWithScript(sendResult)">复制链接+转发话术</el-button>
          <el-button @click="openUrl(sendResult.signUrl)">打开链接</el-button>
          <el-button v-if="sendResult.previewUrl" @click="openUrl(sendResult.previewUrl)">预览合同</el-button>
        </div>
      </div>
      </template>
      <template #footer>
        <el-button @click="resultDialog = false; getData()">关闭并刷新列表</el-button>
      </template>
    </el-dialog>

    <!-- ==================== 合同详情抽屉 ==================== -->
    <el-drawer title="合同详情" v-model="detailDrawer" size="880px">
      <div v-loading="detailLoading" class="detail-wrap">
        <template v-if="detail.task">
          <!-- 基本信息 -->
          <el-divider content-position="left">基本信息</el-divider>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="合同编号">{{ detail.task.contractNo }}</el-descriptions-item>
            <el-descriptions-item label="当前状态">
              <el-tag :type="stateTag(detail.task.state)" size="small" effect="dark">{{ detail.stateName }}</el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="合同模板">{{ detail.task.templateName }}</el-descriptions-item>
            <el-descriptions-item label="签署标题">{{ detail.task.subject }}</el-descriptions-item>
            <el-descriptions-item label="交易对方">{{ detail.task.partyAName }}</el-descriptions-item>
            <el-descriptions-item label="对方代表人">{{ detail.task.partyARep }}</el-descriptions-item>
            <el-descriptions-item label="对方电话">{{ detail.task.partyAPhone }}</el-descriptions-item>
            <el-descriptions-item label="我方（焱火）">{{ detail.task.partyBName }}</el-descriptions-item>
            <el-descriptions-item label="发起时间">{{ formatTime(detail.task.sendTime || detail.task.createTime) }}</el-descriptions-item>
            <el-descriptions-item label="完成时间">{{ formatTime(detail.task.finishTime) }}</el-descriptions-item>
            <el-descriptions-item label="我方角色">
              <el-tag size="small" :type="detail.task.ourRole === 'partyA' ? 'warning' : 'info'" effect="plain">
                {{ detail.task.ourRole === 'partyA' ? '我方＝甲方（占甲方栏）' : '我方＝乙方（占乙方栏）' }}
              </el-tag>
              <span class="mini" style="margin-left: 6px">
                交易对方＝{{ detail.task.ourRole === 'partyA' ? '乙方' : '甲方' }}栏
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="有效期至">
              <span v-if="detail.task.validEnd">{{ formatTime(detail.task.validEnd) }}</span>
              <span v-else>长期有效</span>
            </el-descriptions-item>
            <el-descriptions-item label="发起人">{{ detail.task.createBy }}</el-descriptions-item>
            <el-descriptions-item label="签署任务ID">{{ detail.task.signTaskId }}</el-descriptions-item>
            <el-descriptions-item label="备注" :span="2">{{ detail.task.remark }}</el-descriptions-item>
          </el-descriptions>

          <!-- 双方签署状态 -->
          <el-divider content-position="left">双方签署状态</el-divider>
          <el-table :data="actorRows" border size="small">
            <el-table-column label="参与方" width="170">
              <template #default="scope">
                {{ scope.row.role }}
                <span class="mini">（占合同 {{ scope.row.sideText }}）</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="名称" min-width="180" show-overflow-tooltip></el-table-column>
            <el-table-column label="签署状态" width="110" align="center">
              <template #default="scope">
                <el-tag :type="signStatusTag(scope.row.signStatus)" size="small">{{ signStatusText(scope.row.signStatus) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="joinStatusText" label="加入状态" width="100" align="center"></el-table-column>
            <el-table-column label="签署时间" width="160">
              <template #default="scope">{{ formatTime(scope.row.signTime) }}</template>
            </el-table-column>
          </el-table>

          <!-- 表单内容快照 -->
          <template v-if="formDataRows.length > 0">
            <el-divider content-position="left">表单内容（写入合同的数据）</el-divider>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item v-for="row in formDataRows" :key="row.key" :label="row.key">
                {{ row.value }}
              </el-descriptions-item>
            </el-descriptions>
          </template>

          <!-- 签署记录时间轴 -->
          <el-divider content-position="left">签署记录</el-divider>
          <el-timeline v-if="detail.records && detail.records.length > 0">
            <el-timeline-item
                v-for="(r, idx) in detail.records"
                :key="idx"
                :timestamp="formatTime(r.opTime || r.createTime)"
                :type="r.result === 'fail' ? 'danger' : 'primary'"
                placement="top">
              <div class="record-item">
                <span class="record-action">{{ r.actionName || r.action }}</span>
                <span class="record-meta">
                  <el-tag size="small" effect="plain" v-if="r.actorRole">{{ roleText(r.actorRole) }}</el-tag>
                  <span v-if="r.actorName">{{ r.actorName }}</span>
                  <span v-if="r.operator">操作人：{{ r.operator }}</span>
                </span>
                <div v-if="r.detail" class="record-detail">{{ r.detail }}</div>
              </div>
            </el-timeline-item>
          </el-timeline>
          <div v-else class="tip-empty">暂无签署记录</div>

          <!-- 产物 -->
          <el-divider content-position="left">合同文件与后续操作</el-divider>
          <el-alert
              v-if="!detail.task.partyAPhone && (detail.task.state === 2 || detail.task.state === 3)"
              type="warning"
              :closable="false"
              show-icon
              style="margin-bottom: 10px"
              title="该合同未记录交易对方手机号，法大大未发短信"
              description="请用「复制链接+转发话术」把签署链接手动发给对方；也可点「重发 / 催签」补充手机号后再次通知。">
          </el-alert>
          <div class="detail-actions">
            <el-button v-if="detail.task.previewUrl" @click="openUrl(detail.task.previewUrl)">预览合同</el-button>
            <el-button v-if="detail.task.partyASignUrl && detail.task.state !== 4" type="primary" @click="copyText(detail.task.partyASignUrl)">复制对方签署链接</el-button>
            <el-button v-if="detail.task.state !== 4 && detail.task.state !== 5" type="success" plain @click="copyLinkWithScript(detail.task)">复制链接+转发话术</el-button>
            <el-button v-if="detail.task.state === 2 || detail.task.state === 3" @click="doResend(detail.task)">重发 / 催签</el-button>
            <el-button v-if="detail.task.state === 7" type="danger" plain @click="doRetry(detail.task, true)">重新发起（复用原单）</el-button>
            <el-button v-if="canRestart(detail.task)" type="danger" plain @click="doRestart(detail.task, true)">重新发起（新建合同）</el-button>
            <el-button v-if="detail.task.state === 4" type="success" @click="doDownload(detail.task.contractNo)">下载已签PDF</el-button>
            <el-button @click="doRefresh(detail.task)">刷新状态</el-button>
          </div>
        </template>
      </div>
    </el-drawer>

    <!-- ==================== 发起前预检结果 ==================== -->
    <el-dialog title="发起前预检结果" v-model="precheckDialog" width="720px">
      <el-alert
          v-if="precheckResult"
          :type="precheckResult.ok ? 'success' : 'error'"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
          :title="precheckResult.msg">
        <div>错误 {{ precheckResult.errorCount }} 项 / 提示 {{ precheckResult.warnCount }} 项。
          预检不阻断发起（老版接口允许带提示发起），但「不通过」项建议先解决。</div>
      </el-alert>
      <el-table v-if="precheckResult" :data="precheckResult.items" border size="small">
        <el-table-column prop="name" label="检查项" width="150"></el-table-column>
        <el-table-column label="结果" width="80" align="center">
          <template #default="scope">
            <el-tag :type="levelTag(scope.row.level)" size="small">{{ levelText(scope.row.level) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="detail" label="说明" min-width="340" show-overflow-tooltip></el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="precheckDialog = false">关闭</el-button>
        <el-button type="primary" :disabled="!precheckResult || !precheckResult.ok" @click="precheckDialog = false; doSend()">
          预检通过，继续发起
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  templateList,
  contractStat,
  sendContract,
  retryContract,
  restartContract,
  resendContract,
  getSignUrl,
  getVerifyUrl,
  contractList,
  contractDetail,
  refreshContract,
  getDownloadUrl,
  urgeContract,
  cancelContract,
  regenSignUrl,
  beforeAuthSignUrl,
  verifyContract,
  recheckContract,
  precheckContract,
  partyAAccountCheck,
  signatureList,
  uploadToMinio
} from "../../api/signContract";

export default {
  name: "SignContract",
  data() {
    return {
      // 【2026-09-11】甲方手机号账号/实名预检结果（填完手机号时异步查一次）
      accountCheck: { loading: false, ok: true, msg: "", hasAccount: false, realName: null, realNameState: "" },
      // 【2026-09-11】乙方电子印章列表（从法大大查，用于核对/切换落章印章）
      corpSignatures: [],
      signatureLoading: false,
      tableLoading: false,
      tableData: [],
      templateOptions: [],
      // 总览统计
      stat: {},
      // 自动刷新（30s，页面隐藏时自动暂停）
      autoRefresh: true,
      autoTimer: null,
      stateOptions: [
        {value: 1, label: "待发送"},
        {value: 2, label: "已发送待签署"},
        {value: 3, label: "签署中"},
        {value: 4, label: "已完成"},
        {value: 5, label: "已撤销"},
        {value: 6, label: "已过期"},
        {value: 7, label: "发起失败"},
        {value: 8, label: "已拒签"}
      ],
      query: {
        templateCode: null,
        state: null,
        partyAName: "",
        dateRange: null,
        pageNo: 1,
        pageSize: 10,
        total: 0
      },
      // 发起签署
      sendDialog: false,
      sending: false,
      sendForm: this.emptySendForm(),
      // 【2026-09-14】图片上传中状态：按「图片域名」动态记录 loading（数量随模板 imageFields 变化）
      uploading: {},
      // 发起前预检
      prechecking: false,
      precheckDialog: false,
      precheckResult: null,
      // 发起结果
      resultDialog: false,
      sendResult: {},
      // 详情
      detailDrawer: false,
      detailLoading: false,
      detail: {}
    }
  },
  computed: {
    currentTemplate() {
      return this.templateOptions.find(t => t.templateCode === this.sendForm.templateCode) || {}
    },
    // 【2026-09-14】当前模板的图片上传位（来自模板 imageFields；后端已做 imageSchema→yml 兜底）
    currentImageFields() {
      return this.currentTemplate.imageFields || [];
    },
    // 我方角色文案（决定我方章/手写落甲方栏还是乙方栏）
    ourRoleText() {
      const r = this.currentTemplate.ourRole || this.sendForm.ourRole || "partyB";
      return r === "partyA" ? "我方为甲方" : "我方为乙方";
    },
    // 【2026-09-14】「我方先签」结果弹窗用：我方在 PDF 上的栏位（我方=partyA ⇒ 甲方栏）
    resultMySide() {
      const r = this.sendResult.ourRole || this.currentTemplate.ourRole || "partyB";
      return r === "partyA" ? "甲方" : "乙方";
    },
    // 兼容后端新旧字段：新字段 mySignUrl，旧字段 partyBSignUrl（两者指向同一条我方签署链接）
    resultMySignUrl() {
      return this.sendResult.mySignUrl || this.sendResult.partyBSignUrl || "";
    },
    // 【2026-09-14】我方 / 对方在合同 PDF 上所占的栏位（随「我方角色」对称互换）
    mySideLabel() {
      const r = this.sendForm.ourRole || this.currentTemplate.ourRole || "partyB";
      return r === "partyA" ? "甲方" : "乙方";
    },
    cpSideLabel() {
      return this.mySideLabel === "甲方" ? "乙方" : "甲方";
    },
    // 【2026-09-14】合同正文「甲/乙栏」各写入谁 —— 直接回答运营最关心的问题：
    //   「这两个公司分别填到合同的哪一栏」。依据 = 模板 field_schema（PDF 真实域名 + defaultFrom）
    //   经 our_role 翻转后的实际归属，是把「逻辑方(我方/对方)」与「PDF 栏位(甲/乙栏)」对上的权威口径。
    bodySides() {
      const items = this.parseFieldSchema(this.currentTemplate.fieldSchema);
      const ourIsA = this.mySideLabel === "甲方";
      const myCompany = this.sendForm.partyBName || this.currentTemplate.partyBName || "（我方系统默认公司）";
      const cpCompany = this.sendForm.partyAName || "（未填）";
      return ["A", "B"].map(ch => {
        const side = ch === "A" ? "甲方" : "乙方";
        // 该栏里「管主体名称」的域：域名属于本栏，且（按角色翻转后）来源指向 *Name
        const hit = items.find(it => {
          const k = String((it && it.key) || "");
          const inSide = ch === "A"
              ? /^txtComA|^txtPartyA/i.test(k)
              : /^txtComB|^txtPartyB/i.test(k);
          if (!inSide) return false;
          const src = this.flipPartyFrom(it.defaultFrom, ourIsA);
          return src === "partyAName" || src === "partyBName";
        });
        if (!hit) {
          return {side: side, bound: false, owner: "该模板 PDF 无此栏的主体名称域", company: ""};
        }
        const src = this.flipPartyFrom(hit.defaultFrom, ourIsA);
        const isMine = src === "partyBName";
        return {
          side: side,
          bound: true,
          key: hit.key,
          owner: isMine ? "我方（焱火）" : "交易对方",
          company: isMine ? myCompany : cpCompany
        };
      });
    },
    // 模板 PDF 里是否真的存在「放对方地址 / 放我方地址」的域 —— 决定对应输入框填了有没有用
    cpAddressBound() {
      return this.hasBoundField("partyAAddress");
    },
    myAddressBound() {
      return this.hasBoundField("partyBAddress");
    },
    // 我方落章所用的关键字 —— 关键字是「栏位制」：我方=甲方 ⇒ 用 sign_keyword_a（甲方栏关键字）。
    //   优先取后端算好的 effective*（已含 yml 全局回退），取不到再退回模板值 / 栏位默认文案。
    myKeyword() {
      const t = this.currentTemplate;
      return this.mySideLabel === "甲方"
          ? (t.effectiveSignKeywordA || t.signKeywordA || "甲方：")
          : (t.effectiveSignKeywordB || t.signKeywordB || "乙方：");
    },
    // 【2026-09-14】我方（焱火）签署要素文案 —— 由模板 party_b_auto_sign 推导（恒指我方）
    myElementsText() {
      const m = this.currentTemplate.partyBAutoSign;
      const mode = (m === null || m === undefined) ? 1 : Number(m);
      if (mode === 2) return "自动盖章 + 授权负责人手写签字";
      if (mode === 0) return "仅授权负责人手写签字";
      return "仅自动盖章";
    },
    // 【2026-09-14】对方（交易对手）签署要素文案 —— 由 party_a_seal / party_a_hand 推导（恒指对方）
    //   ⚠️ 对方是自然人时恒需手写（没有公章可盖），模板的 party_a_hand 不生效 —— 与后端 aHandOn 判定一致
    cpElementsText() {
      const t = this.currentTemplate;
      const isPerson = this.sendForm.partyAType === "person";
      const seal = Number(t.partyASeal) === 1;
      // 【2026-09-14】对方企业章两种模式：填了印章ID=我方代盖；留空（常态）= 对方在链接里自助盖章
      const sealByUs = seal && !!(t.partyASealSignatureId || "").trim();
      const sealText = sealByUs ? "企业盖章（我方代盖）" : "企业盖章（对方在签署链接中自助盖）";
      const hand = isPerson
          ? true
          : (t.partyAHand === null || t.partyAHand === undefined ? true : Number(t.partyAHand) === 1);
      if (seal && hand) return sealText + " + 授权负责人手写签字";
      if (seal) return "仅" + sealText;
      if (hand) return "仅授权负责人手写签字";
      return "⚠️ 未配置任何签署要素（合同每方至少要「盖章」或「手写签字」其一）";
    },
    // 已配置好（PDF 来源 + 乙方印章ID）、可以直接发起的模板
    readyTemplates() {
      return this.templateOptions.filter(t => t.ready !== false)
    },
    // 按模板 fieldSchema 动态渲染表单
    // 【2026-09-11】拆成 allFormFields（原样）/ formFields（过滤 corpOnly 后）两层：
    // 自然人甲方时，「甲方公司名称」这种 corpOnly 域整项隐藏 ——
    // 隐藏之外还要能拿到原 schema 去**清掉残留值**，否则切换甲方类型后旧值会继续写进合同。
    allFormFields() {
      const schema = this.currentTemplate.fieldSchema;
      if (!schema) return [];
      try {
        const arr = typeof schema === "string" ? JSON.parse(schema) : schema;
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        console.warn("fieldSchema 解析失败", e);
        return [];
      }
    },
    formFields() {
      const person = this.sendForm.partyAType === "person";
      return this.allFormFields.filter(f => !(person && f.corpOnly === true));
    },
    // 【2026-09-12】服务起止日（txtStartDay / txtEndDay）**不再隐藏**：
    //   它是合同实质条款（「服务时间：2026年9月__日至2026年9月__日，共5天」），
    //   旧版把它俩当静默域、默认填「今天 → 次月同日」，而模板两个占位共用同一个月，
    //   于是签出来的是「9月12日至9月12日，共5天」——条款自相矛盾。
    //   现在只保留「乙方联系电话」静默（来源 = ④ 区输入 / yml 默认）。
    silentFormFields() {
      return this.formFields.filter(f =>
          f.defaultFrom || f.hidden === true ||
          /^txtComBPhone$/i.test(f.key || ""));
    },
    manualFormFields() {
      const silent = this.silentFormFields;
      return this.formFields.filter(f => !silent.includes(f));
    },
    // 剩余手填项按【PDF 栏位】分组（key 前缀规则，schema 可用 group 字段显式指定）
    // 【2026-09-14】标题按「栏位」而非「身份」命名 —— A/B 分组取自域名前缀（txtComA / txtComB），
    //   属栏位维度；谁是"我方"由 our_role 决定，故标题只标栏位并注明该栏当前归谁，
    //   避免与 ②/④ 区的身份口径打架。
    manualGroups() {
      const fs = this.manualFormFields;
      const pick = pred => fs.filter(pred);
      const grp = f => (f.group === "A" || f.group === "B") ? f.group
          : (/^(txtComA|txtCampus|txtPartyA)/i.test(f.key || "") ? "A"
              : (/^(txtComB|txtPartyB)/i.test(f.key || "") ? "B" : "O"));
      const a = pick(f => grp(f) === "A");
      const b = pick(f => grp(f) === "B");
      const o = pick(f => grp(f) === "O");
      const ourIsA = this.mySideLabel === "甲方";
      const groups = [];
      if (a.length) groups.push({title: "合同甲方栏信息（本模板：" + (ourIsA ? "我方" : "交易对方") + "）", fields: a});
      if (b.length) groups.push({title: "合同乙方栏信息（本模板：" + (ourIsA ? "交易对方" : "我方") + "）", fields: b});
      if (o.length) groups.push({title: a.length || b.length ? "其他信息" : "", fields: o});
      return groups;
    },
    // 详情里的表单内容快照
    formDataRows() {
      const raw = this.detail.task && this.detail.task.formData;
      if (!raw) return [];
      try {
        const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Object.keys(obj || {}).map(k => ({key: k, value: obj[k]}));
      } catch (e) {
        return [];
      }
    },
    // 详情里的双方参与方行
    // 【2026-09-14】「参与方」列不再用甲/乙 —— 因为甲/乙是 PDF 栏位名，随 our_role 互换，
    //   放在这里只会让人误读。恒用「交易对方 / 我方」身份口径，栏位名在「我方角色」项里单独标注。
    actorRows() {
      const t = this.detail.task;
      if (!t) return [];
      const ourIsA = t.ourRole === "partyA";
      return [
        {
          role: "交易对方",
          name: t.partyAName,
          joinStatusText: t.partyAJoinStatus === "joined" ? "已加入" : (t.partyAJoinStatus === "no_join" ? "未加入" : "-"),
          signStatus: t.partyASignStatus,
          signTime: t.partyASignTime,
          sideText: (ourIsA ? "乙方" : "甲方") + "栏"
        },
        {
          role: "我方（焱火）",
          name: t.partyBName,
          joinStatusText: "-",
          signStatus: t.partyBSignStatus,
          signTime: t.partyBSignTime,
          sideText: (ourIsA ? "甲方" : "乙方") + "栏"
        }
      ];
    }
  },
  watch: {
    // 甲方/乙方信息变化时，自动把值同步到「表单内容」里声明了 defaultFrom 的字段
    // （例如 txtComA=甲方公司名称、txtComAPhone=甲方联系电话），避免同一份信息填两遍
    "sendForm.partyAName": "applyFieldDefaults",
    "sendForm.partyAPhone": "applyFieldDefaults",
    "sendForm.partyAAddress": "applyFieldDefaults",
    "sendForm.partyARep": "applyFieldDefaults",
    "sendForm.partyBName": "applyFieldDefaults",
    // 【2026-09-11 补】乙方授权代表人 / 联系电话 → 合同正文同名栏位联动
    "sendForm.partyBRep": "applyFieldDefaults",
    "sendForm.partyBPhone": "applyFieldDefaults",
    // 切换甲方类型（企业/自然人）时要重跑一次：自然人需隐藏并清空 corpOnly 域（甲方公司名称等）
    "sendForm.partyAType": "applyFieldDefaults",
    // 【2026-09-11 补】field_schema 配了 defaultFrom: subject 的字段（如合同标题域）要随标题输入联动
    "sendForm.subject": "applyFieldDefaults"
  },
  methods: {
    /**
     * 甲方手机号账号/实名预检（2026-09-11 新增）。
     * 只读接口，不会创建合同、不发短信。目的：填完手机号就知道"发起后甲方会不会被要求实名"。
     * 【2026-09-11 修正】个人实名改由后端探 query_signature 得到明确结论（已实名/未实名/未知），
     * 不再依赖 get_auth_status（该接口对个人客户号恒返回 0，无区分度）。
     */
    async checkPartyAAccount() {
      const phone = (this.sendForm.partyAPhone || "").trim();
      if (!/^1\d{10}$/.test(phone)) {
        this.accountCheck = { loading: false, ok: true, msg: "" };
        return;
      }
      this.accountCheck = { loading: true, ok: true, msg: "" };
      try {
        const res = await partyAAccountCheck({
          partyAPhone: phone,
          partyAType: this.sendForm.partyAType,
          partyACustomerId: this.sendForm.partyACustomerId
        });
        const d = (res && res.data) || {};
        this.accountCheck = {
          loading: false,
          ok: true,
          msg: d.msg || "",
          hasAccount: d.hasAccount === true,
          realName: typeof d.realName === "boolean" ? d.realName : null,
          realNameState: d.realNameState || ""
        };
        // 已有客户号且我们在库里查到过 → 顺手回填，避免发起时再走一次自动注册
        if (d.fromHistory && d.customerId && !this.sendForm.partyACustomerId) {
          this.sendForm.partyACustomerId = d.customerId;
        }
      } catch (e) {
        this.accountCheck = { loading: false, ok: false, msg: "账号预检失败：" + (e && e.message ? e.message : e) };
      }
    },
    /** 拉取乙方（我方企业）在法大大平台的可用印章列表，用于核对落章印章有没有配错 */
    async loadCorpSignatures() {
      this.signatureLoading = true;
      try {
        const res = await signatureList(null, this.sendForm.templateCode);
        const d = (res && res.data) || {};
        this.corpSignatures = d.signatures || [];
        if (d.usingSignatureId && d.usingSignatureFound === false) {
          this.$message.warning("当前模板配置的印章ID在法大大平台上查不到，请核对 corpSignatureId");
        }
      } catch (e) {
        this.corpSignatures = [];
      } finally {
        this.signatureLoading = false;
      }
    },
    emptySendForm() {
      return {
        templateCode: null,
        subject: "",
        partyAType: "corp",
        partyAName: "",
        partyARep: "",
        partyAPhone: "",
        partyAAddress: "",
        partyACustomerId: "",
        partyBName: "",
        // 【2026-09-11】乙方（我方）授权代表人与联系电话：写进合同正文对应两栏，留空则不填
        partyBRep: "",
        partyBPhone: "",
        // 【2026-09-14】我方地址：仅模板 our_role=partyA（我方=甲方）时参与 PDF「甲方栏地址」填充
        partyBAddress: "",
        formData: {},
        // 【2026-09-14】图片上传位：key = 模板 imageFields[].name（PDF 识别出的图片域），
        //   数量由模板动态决定（「PDF 里几个图片域 → 几个上传位」），上传后回填 MinIO URL；留空不渲染。
        images: {},
        remark: ""
      };
    },
    // ---------- 时间/状态格式化 ----------
    formatTime(v) {
      if (!v) return "-";
      if (typeof v === "string") return v.replace("T", " ").substring(0, 19);
      try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v);
        const p = n => (n < 10 ? "0" + n : "" + n);
        return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate()) + " " +
            p(d.getHours()) + ":" + p(d.getMinutes()) + ":" + p(d.getSeconds());
      } catch (e) {
        return String(v);
      }
    },
    signStatusText(s) {
      const map = {
        wait_sign: "待签署",
        signed: "已签署",
        sign_rejected: "已拒签",
        no_join: "未加入",
        joined: "已加入",
        signing: "签署中"
      };
      return map[s] || (s ? s : "-");
    },
    signStatusTag(s) {
      if (s === "signed") return "success";
      if (s === "sign_rejected") return "danger";
      if (s === "wait_sign" || s === "signing") return "warning";
      return "info";
    },
    stateTag(state) {
      const map = {1: "info", 2: "warning", 3: "warning", 4: "success", 5: "info", 6: "info", 7: "danger", 8: "danger"};
      return map[state] || "info";
    },
    roleText(role) {
      // 【2026-09-14】签署记录里的 A/B 是法大大「逻辑方」：A = 交易对方、B = 我方（与 PDF 栏位无关）。
      //   历史显示「甲方/乙方」会把栏位名与身份混为一谈，故改身份口径。
      const map = {A: "交易对方", B: "我方（焱火）", SYS: "系统"};
      return map[role] || role;
    },
    // 是否还能取签署链接（仅进行中的 1/2/3 有意义；4/5/7/8 是终态，7/8 走「重新发起」）
    canSign(row) {
      return row.state !== 4 && row.state !== 5 && row.state !== 7 && row.state !== 8;
    },
    // 是否可以「重新发起（新建合同）」——老版已上传的合同内容不可改，拒签/撤销/过期/失败后只能换新合同重签
    canRestart(row) {
      return row && (row.state === 5 || row.state === 6 || row.state === 7 || row.state === 8);
    },
    // 跳到「合同模板管理」页面（换合同只改模板，不改代码）
    goTemplateManage() {
      this.$router.push("/signContractTemplate");
    },
    // ---------- 列表 ----------
    handlePageChange(val) {
      this.query.pageNo = val;
      this.getData();
    },
    handleSizeChange(val) {
      this.query.pageSize = val;
      this.query.pageNo = 1;
      this.getData();
    },
    handleSearch() {
      this.query.pageNo = 1;
      this.getData();
    },
    handleReset() {
      this.query.templateCode = null;
      this.query.state = null;
      this.query.partyAName = "";
      this.query.dateRange = null;
      this.query.pageNo = 1;
      this.getData();
    },
    getData() {
      this.tableLoading = true;
      this.getStat();
      contractList(this.buildQueryParams()).then(val => {
        this.tableLoading = false;
        if (val && val.code === 200) {
          this.tableData = val.data.records || [];
          this.query.total = val.data.total || 0;
        } else {
          this.$message.error((val && val.message) || "获取签署列表失败");
        }
      }).catch(() => {
        this.tableLoading = false;
        this.$message.error("获取签署列表失败");
      });
    },
    // ---------- 总览统计 ----------
    getStat() {
      contractStat().then(val => {
        if (val && val.code === 200) {
          this.stat = val.data || {};
        }
      }).catch(() => {
      });
    },
    // ---------- 自动刷新 ----------
    onAutoRefreshChange(val) {
      if (val) {
        this.startAutoRefresh();
        this.$message.success("已开启自动刷新（每 30 秒）");
      } else {
        this.stopAutoRefresh();
        this.$message.info("已关闭自动刷新");
      }
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.autoTimer = setInterval(() => {
        // 页面切到后台时不打扰，回来再刷
        if (document.visibilityState === "visible") {
          this.getDataSilent();
        }
      }, 30000);
    },
    stopAutoRefresh() {
      if (this.autoTimer) {
        clearInterval(this.autoTimer);
        this.autoTimer = null;
      }
    },
    // 静默刷新（不显示 loading、不弹错误），避免打扰正在操作的人
    getDataSilent() {
      const params = this.buildQueryParams();
      contractList(params).then(val => {
        if (val && val.code === 200) {
          this.tableData = val.data.records || [];
          this.query.total = val.data.total || 0;
        }
      }).catch(() => {
      });
      this.getStat();
    },
    buildQueryParams() {
      return {
        templateCode: this.query.templateCode || undefined,
        state: this.query.state === null ? undefined : this.query.state,
        partyAName: this.query.partyAName || undefined,
        startTime: this.query.dateRange && this.query.dateRange[0] ? this.query.dateRange[0] : undefined,
        endTime: this.query.dateRange && this.query.dateRange[1] ? this.query.dateRange[1] : undefined,
        pageNo: this.query.pageNo,
        pageSize: this.query.pageSize
      };
    },
    // ---------- 角色/栏位换算（务必与后端 FddSignContractService.flipPartyFrom 保持同步） ----------
    /**
     * our_role=partyA（我方=甲方）时，合同 PDF 的甲/乙栏归属对调 ⇒
     * defaultFrom 的 partyA*(交易对方) 与 partyB*(我方) 两个系列整体互换。
     * our_role=partyB（历史默认）时原样返回 —— 保证历史模板行为零变化。
     */
    flipPartyFrom(from, ourIsA) {
      if (!from) return "";
      if (!ourIsA) return from;
      const map = {
        partyAName: "partyBName", partyAPhone: "partyBPhone",
        partyAAddress: "partyBAddress", partyARep: "partyBRep",
        partyBName: "partyAName", partyBPhone: "partyAPhone",
        partyBAddress: "partyAAddress", partyBRep: "partyARep"
      };
      return map[from] || from;
    },
    // fieldSchema 可能是 JSON 字符串或数组，统一解析成数组
    parseFieldSchema(raw) {
      if (!raw) return [];
      try {
        const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    },
    // 模板里是否存在「按角色翻转后来源 = target」的域（用于判断某个输入框填了会不会渲染）
    hasBoundField(target) {
      const ourIsA = this.mySideLabel === "甲方";
      return this.parseFieldSchema(this.currentTemplate.fieldSchema)
          .some(it => this.flipPartyFrom(it && it.defaultFrom, ourIsA) === target);
    },
    // ---------- 模板 ----------
    getTemplates() {
      templateList().then(val => {
        if (val && val.code === 200) {
          this.templateOptions = val.data || [];
        }
      });
    },
    // ---------- 发起签署 ----------
    openSendDialog() {
      this.sendForm = this.emptySendForm();
      if (this.templateOptions.length === 0) {
        this.getTemplates();
      }
      // 只有一个可直接发起的模板时自动选中，少点一次
      const ready = this.readyTemplates;
      if (ready.length === 1) {
        this.sendForm.templateCode = ready[0].templateCode;
      }
      this.applyFieldDefaults();
      this.sendDialog = true;
    },
    onTemplateChange() {
      // 切换模板时重置表单内容，避免上一个模板的字段残留
      this.sendForm.formData = {};
      this.sendForm.partyBName = "";
      this.applyFieldDefaults();
    },
    /**
     * 按模板 fieldSchema 的 default / defaultFrom 给「表单内容」补默认值。
     * key 是 PDF 里真实的表单域名字（如 txtComA），后端会把这些值写进 PDF 正文。
     * defaultFrom 取值：partyAName / partyAPhone / partyAAddress / partyARep / partyBName /
     *                  partyBRep / partyBPhone / contractNo / subject / today / today.year / ...
     */
    currentDefaultsPool() {
      const now = new Date();
      const nm = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
      const p2 = n => (n < 10 ? "0" + n : "" + n);
      return {
        partyAName: this.sendForm.partyAName,
        partyAPhone: this.sendForm.partyAPhone,
        partyAAddress: this.sendForm.partyAAddress,
        // 【2026-09-11 冗余清理】自然人无代表人输入框，自动取甲方姓名
        partyARep: this.sendForm.partyARep
            || (this.sendForm.partyAType === "person" ? this.sendForm.partyAName : ""),
        partyBName: this.sendForm.partyBName || this.currentTemplate.partyBName || "",
        // 【2026-09-11 新增】乙方（我方）授权代表人与联系电话，写进合同正文对应两栏
        partyBRep: this.sendForm.partyBRep || "",
        partyBPhone: this.sendForm.partyBPhone || "",
        // 【2026-09-14】我方地址（our_role=partyA 时合同「甲方栏」的地址取这里）
        partyBAddress: this.sendForm.partyBAddress || "",
        subject: this.sendForm.subject,
        today: now.getFullYear() + "-" + p2(now.getMonth() + 1) + "-" + p2(now.getDate()),
        "today.year": String(now.getFullYear()),
        "today.month": String(now.getMonth() + 1),
        "today.day": String(now.getDate()),
        // 【2026-09-11】次月同日（服务结束日默认：今天起一个月内）；与后端 Calendar 加月规则一致
        "plus1month.year": String(nm.getFullYear()),
        "plus1month.month": String(nm.getMonth() + 1),
        "plus1month.day": String(nm.getDate())
      };
    },
    applyFieldDefaults() {
      // 【2026-09-14】our_role=partyA（我方=甲方）⇒ 合同甲/乙栏归属对调，
      //   取值来源系列 partyA*/partyB* 必须整体互换（与后端 flipPartyFrom 完全一致），
      //   否则前端预填与后端最终写入 PDF 的值会不一致。
      const ourIsA = this.mySideLabel === "甲方";
      // 自然人甲方：corpOnly 域（如「甲方公司名称」）先清空 ——
      // 这些域上一轮可能已被自动带出过值，切换甲方类型后不擦掉就会把旧值写进新合同。
      // our_role=partyA 时 A 类域填的是「我方(焱火)」，企业主体恒成立 ⇒ 不清空。
      if (this.sendForm.partyAType === "person" && !ourIsA) {
        for (const f of this.allFormFields) {
          if (f.corpOnly === true && f.key) {
            this.sendForm.formData[f.key] = "";
          }
        }
      }
      const pool = this.currentDefaultsPool();
      for (const f of this.formFields) {
        if (!f.key) continue;
        if (f.corpOnly === true && this.sendForm.partyAType === "person" && !ourIsA) continue;
        // 【2026-09-12 P0 修复】配了 defaultFrom 的域是「派生域」——它的值唯一来源于上方输入框
        //  （甲方名称/电话/地址、乙方名称、乙方电话、签订日期…），这些域在界面上全部是隐藏的
        //  （见 silentFormFields），运营不可能手工改。
        //  因此必须**以来源为准强制覆盖**，绝不能「已有值就跳过」。
        //  真实事故 HT20260912191230534：甲方联系电话域残留了 "1"，之后把甲方手机号改成
        //  13673710064 也再不会同步 → 签出来的合同正文印着「联系电话：1」。
        //  手填域（无 defaultFrom）仍遵循「已有值不覆盖」，避免冲掉运营的输入。
        if (f.defaultFrom) {
          // our_role=partyA：先按角色翻转取值来源，再取池中值
          const srcKey = this.flipPartyFrom(f.defaultFrom, ourIsA);
          const src = pool[srcKey];
          this.sendForm.formData[f.key] = (src === undefined || src === null) ? "" : src;
          continue;
        }
        const cur = this.sendForm.formData[f.key];
        if (cur !== undefined && cur !== null && cur !== "") continue;
        const dv = f.default;
        if (dv !== undefined && dv !== null && dv !== "") {
          // Vue3 响应式是 Proxy，直接加新 key 即可触发更新
          this.sendForm.formData[f.key] = dv;
        }
      }
      // 【2026-09-12 修正】服务起止日 = 合同实质条款（「服务时间为 X 日至 Y 日，共5天」），
      //  必须由运营显式填写，不能静默猜。
      //  旧逻辑把它当静默域、默认「今天 → 次月同日」，结果在模板里渲染成
      //  「2026年9月12日至2026年9月12日，共5天」这种自相矛盾的条款
      //  —— 因为模板两个占位共用同一个月，次月同日会串到本月。
      //  现在只做「空值兜底」：开始日=今天，结束日=今天+4（服务期 5 天），
      //  并保证不跨月（超出本月则夹到月末），运营可在「其他信息」区改。
      const pool2 = this.currentDefaultsPool();
      const dayNum = n => parseInt(n, 10);
      const monthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
      const todayD = dayNum(pool2["today.day"]) || 1;
      const suggestEnd = Math.min(todayD + 4, monthEnd);
      for (const f of this.formFields) {
        if (!f.key) continue;
        const k = f.key.toLowerCase();
        if (k.includes("startday") && !this.sendForm.formData[f.key]) {
          this.sendForm.formData[f.key] = String(todayD);
        } else if (k.includes("endday") && !this.sendForm.formData[f.key]) {
          this.sendForm.formData[f.key] = String(suggestEnd);
        } else if (k.includes("bphone") && !this.sendForm.formData[f.key]) {
          // 正文「乙方栏联系电话」：our_role=partyA 时这一栏放的是交易对方 ⇒ 取对方电话
          this.sendForm.formData[f.key] = ourIsA ? (pool2["partyAPhone"] || "")
              : (pool2["partyBPhone"] || "");
        }
      }
    },
    doSend() {
      if (!this.sendForm.templateCode) {
        this.$message.warning("请先选择合同模板");
        return;
      }
      if (this.currentTemplate.ready === false) {
        this.$message.error(this.currentTemplate.unreadyReason || "该合同模板还没配置好（需要 PDF 来源 + 我方电子印章ID），不能发起");
        return;
      }
      if (!this.sendForm.partyAName) {
        this.$message.warning("请填写" + this.cpSideLabel + "名称");
        return;
      }
      // 只校验人工可见的手填项；静默字段（自动带出/服务起止日）由 applyFieldDefaults 兜底
      for (const f of this.manualFormFields) {
        if (f.required === true && !this.sendForm.formData[f.key]) {
          this.$message.warning("请填写：" + (f.label || f.key));
          return;
        }
      }
      // 未填手机号 → 法大大不会自动发短信，先确认一次（避免签完才发现对方没收到）
      if (!this.sendForm.partyAPhone) {
        this.$confirm(
            "未填写" + this.cpSideLabel + "手机号，法大大不会自动发送签署短信，需要你手动把签署链接转发给"
            + this.cpSideLabel + "。确定继续发起吗？",
            "确认发起", {type: "warning", confirmButtonText: "继续发起", cancelButtonText: "我去填手机号"})
            .then(() => this.submitSend())
            .catch(() => {
            });
        return;
      }
      this.submitSend();
    },
    submitSend() {
      const dto = Object.assign({}, this.sendForm);
      dto.formData = this.cleanFormData(dto.formData);

      this.sending = true;
      sendContract(dto).then(val => {
        this.sending = false;
        if (val && val.code === 200 && val.data && val.data.code === "1") {
          this.sendResult = val.data;
          this.sendDialog = false;
          this.resultDialog = true;
          this.getData();
        } else {
          const msg = (val && (val.message || (val.data && val.data.msg))) || "发起签署失败";
          this.$message.error(msg);
          // 发起失败的行会出现在列表里，刷新让用户能直接看到并「重新发起」
          this.getData();
        }
      }).catch(() => {
        this.sending = false;
        this.$message.error("发起签署失败");
        this.getData();
      });
    },
    // ---------- 图片表单域（身份证 / 学历 / 银行卡…）上传到 MinIO ----------
    beforeImg(file) {
      const ok = file.type && file.type.startsWith("image/");
      if (!ok) {
        this.$message.error("请选择图片文件");
        return false;
      }
      return true;
    },
    // name = 模板 imageFields[].name（PDF 里那个图片域的名字），写入 sendForm.images[name]
    uploadSignImage({file}, name) {
      this.uploading[name] = true;
      uploadToMinio(file, "signAttach").then(res => {
        this.uploading[name] = false;
        if (res && res.code === 200 && res.data) {
          if (!this.sendForm.images) this.sendForm.images = {};
          this.sendForm.images[name] = res.data;
          this.$message.success("已上传并回填");
        } else {
          this.$message.error((res && res.message) || "上传失败");
        }
      }).catch(() => {
        this.uploading[name] = false;
        this.$message.error("上传失败");
      });
    },
    // ---------- 合同详情 ----------
    openDetail(row) {
      this.detailDrawer = true;
      this.detailLoading = true;
      this.detail = {};
      contractDetail(row.contractNo, true).then(val => {
        this.detailLoading = false;
        if (val && val.code === 200 && val.data && val.data.code === "1") {
          this.detail = val.data;
        } else {
          this.$message.error((val && (val.message || (val.data && val.data.msg))) || "查询合同详情失败");
        }
      }).catch(() => {
        this.detailLoading = false;
        this.$message.error("查询合同详情失败");
      });
    },
    // ---------- 操作 ----------
    doRefresh(row) {
      refreshContract(row.contractNo).then(val => {
        if (val && val.code === 200) {
          this.$message.success("状态已刷新：" + ((val.data && val.data.stateName) || ""));
          this.getData();
          if (this.detailDrawer) {
            this.openDetail({contractNo: row.contractNo});
          }
        } else {
          this.$message.error((val && val.message) || "刷新失败");
        }
      }).catch(() => this.$message.error("刷新失败"));
    },
    // 甲方未实名时的处置：取实名链接并复制给运营转发。
    // 老版 extsign 不校验实名，未实名的客户号照样出链接，甲方点开是「尚未实名认证」死页，
    // 此时唯一的解法就是让甲方先走实名，完成后原签署链接自动可用。
    copyVerifyUrl(row) {
      getVerifyUrl(row.contractNo).then(val => {
        if (val && val.code === 200 && val.data) {
          if (val.data.verified) {
            this.$message.success("交易对方已完成实名认证，可直接签署");
            this.getData();
            return;
          }
          if (val.data.verifyUrl) {
            this.copyText(val.data.verifyUrl);
            this.$message({
              type: "warning",
              duration: 8000,
              showClose: true,
              message: "交易对方尚未实名，实名链接已复制。请发给对方完成实名，之后原签署链接即可正常签署，无需重新发起合同。"
            });
            return;
          }
          this.$message.error((val.data && val.data.msg) || "未取到实名链接");
        } else {
          this.$message.error((val && val.message) || "未取到实名链接");
        }
      }).catch(() => this.$message.error("未取到实名链接"));
    },
    copySignUrl(row) {
      getSignUrl(row.contractNo).then(val => {
        if (val && val.code === 200 && val.data && val.data.signUrl) {
          this.copyText(val.data.signUrl);
        } else {
          this.$message.error((val && val.message) || "未取到签署链接");
        }
      }).catch(() => this.$message.error("未取到签署链接"));
    },
    // ---------- 重新发起（发起失败的行，复用原填写内容） ----------
    doRetry(row, fromDetail) {
      this.$confirm("将复用该合同已填写的交易对方信息与表单内容，重新向法大大发起签署。确定继续吗？",
          "重新发起", {type: "warning", confirmButtonText: "重新发起", cancelButtonText: "取消"})
          .then(() => {
            retryContract(row.contractNo).then(val => {
              if (val && val.code === 200 && val.data && val.data.code === "1") {
                this.$message.success("已重新发起成功");
                this.sendResult = val.data;
                this.resultDialog = true;
                if (fromDetail) {
                  this.detailDrawer = false;
                }
                this.getData();
              } else {
                this.$message.error((val && (val.message || (val.data && val.data.msg))) || "重新发起失败");
                this.getData();
              }
            }).catch(() => this.$message.error("重新发起失败"));
          }).catch(() => {
      });
    },
    // ---------- 重发 / 催签（可补充手机号） ----------
    doResend(row) {
      this.$prompt("可补充/更正交易对方手机号（留空则不修改）。提交后将重新通知对方并获取最新签署链接。",
          "重发 / 催签", {
            confirmButtonText: "重发并催签",
            cancelButtonText: "取消",
            inputPlaceholder: "交易对方手机号，如 13800000000",
            inputValue: row.partyAPhone || ""
          }).then(({value}) => {
        resendContract(row.contractNo, value || "").then(val => {
          if (val && val.code === 200 && val.data && val.data.code === "1") {
            this.$message.success(val.data.urged
                ? "已催签，并已刷新签署链接（已复制到剪贴板）"
                : "已刷新签署链接（催签未生效，建议直接转发链接）");
            if (val.data.signUrl) {
              this.copyText(val.data.signUrl);
            }
            this.getData();
            if (this.detailDrawer) {
              this.openDetail({contractNo: row.contractNo});
            }
          } else {
            this.$message.error((val && (val.message || (val.data && val.data.msg))) || "重发失败");
          }
        }).catch(() => this.$message.error("重发失败"));
      }).catch(() => {
      });
    },
    // ---------- 链接 + 转发话术 ----------
    fetchSignUrl(contractNo) {
      return getSignUrl(contractNo).then(val => {
        if (val && val.code === 200 && val.data && val.data.signUrl) {
          return val.data.signUrl;
        }
        this.$message.error((val && val.message) || "未取到签署链接");
        return null;
      }).catch(() => {
        this.$message.error("未取到签署链接");
        return null;
      });
    },
    copyLinkWithScript(row) {
      if (row && row.partyASignUrl) {
        this.copyText(this.buildScript(row, row.partyASignUrl));
      } else {
        this.fetchSignUrl(row.contractNo).then(url => {
          if (url) {
            this.copyText(this.buildScript(row, url));
          }
        });
      }
    },
    buildScript(row, url) {
      const tplObj = this.templateOptions.find(t => t.templateCode === row.templateCode) || {};
      const tplName = tplObj.templateName || row.templateName || "合同";
      return [
        "【" + tplName + "】签署邀请",
        "您好，我方（郑州小奔焱火体育管理有限公司）已就本合同发起线上签署，",
        "请点开下方专属链接，完成实名核验后签署：",
        url,
        "",
        "说明：",
        "1. 链接为贵方专属签署入口，请勿转发他人；",
        "2. 企业签署需完成企业实名核验（法定代表人或经办人）；",
        "3. 签署完成后我方企业印章自动加盖，合同即生效。"
      ].join("\n");
    },
    doDownload(contractNo) {
      getDownloadUrl(contractNo).then(val => {
        if (val && val.code === 200 && val.data && val.data.downloadUrl) {
          this.openUrl(val.data.downloadUrl);
        } else {
          this.$message.error((val && val.message) || "暂无可下载的已签PDF");
        }
      }).catch(() => this.$message.error("获取下载地址失败"));
    },
    // 注：老版法大大不提供「出证报告」接口，相关入口与提示已整体移除，勿再补回。
    // ---------- 重新发起（新建一份合同，拒签/撤销/过期/失败后用） ----------
    doRestart(row, fromDetail) {
      const why = row.partyASignStatus === "sign_rejected" ? "交易对方已拒签"
          : (row.state === 8 ? "已拒签" : (row.state === 5 ? "已撤销" : (row.state === 6 ? "已过期" : "发起失败")));
      this.$confirm("原合同为「" + why + "」。老版法大大已上传的合同内容无法修改，"
          + "因此将新建一份合同（新合同编号）并复用原交易对方信息与表单内容重新发起。确定继续吗？",
          "重新发起（新建合同）", {type: "warning", confirmButtonText: "新建并发起", cancelButtonText: "取消"})
          .then(() => {
            restartContract(row.contractNo).then(val => {
              if (val && val.code === 200 && val.data && val.data.code === "1") {
                this.$message.success("已新建合同并发起成功");
                this.sendResult = val.data;
                this.resultDialog = true;
                if (fromDetail) {
                  this.detailDrawer = false;
                }
                this.getData();
              } else {
                this.$message.error((val && (val.message || (val.data && val.data.msg))) || "重新发起失败");
                this.getData();
              }
            }).catch(() => this.$message.error("重新发起失败"));
          }).catch(() => {});
    },
    // ---------- 发起前预检 ----------
    doPrecheckSend() {
      if (!this.sendForm.templateCode) {
        this.$message.warning("请先选择合同模板");
        return;
      }
      const dto = Object.assign({}, this.sendForm);
      dto.formData = this.cleanFormData(dto.formData);
      this.prechecking = true;
      precheckContract(dto).then(val => {
        this.prechecking = false;
        if (val && val.code === 200 && val.data) {
          this.precheckResult = val.data;
          this.precheckDialog = true;
        } else {
          this.$message.error((val && val.message) || "预检失败");
        }
      }).catch(() => {
        this.prechecking = false;
        this.$message.error("预检失败");
      });
    },
    cleanFormData(formData) {
      return Object.keys(formData || {})
          .filter(k => formData[k] !== "" && formData[k] !== null && formData[k] !== undefined)
          .reduce((acc, k) => {
            acc[k] = formData[k];
            return acc;
          }, {});
    },
    levelTag(level) {
      if (level === "error") return "danger";
      if (level === "warn") return "warning";
      return "success";
    },
    levelText(level) {
      if (level === "error") return "不通过";
      if (level === "warn") return "提示";
      return "通过";
    },
    doUrge(row) {
      this.$confirm("确定向未签署的参与方发送催签通知吗？", "催签", {type: "warning"}).then(() => {
        urgeContract(row.contractNo).then(val => {
          if (val && val.code === 200) {
            this.$message.success("催签已发送");
          } else {
            this.$message.error((val && val.message) || "催签失败");
          }
        });
      }).catch(() => {});
    },
    doCancel(row) {
      this.$prompt("请输入撤销原因（可选）。⚠️ 老版法大大没有在线撤销接口，这里只把本地状态标记为「已撤销」，"
          + "交易对方手上的链接仍然有效（实际作废需登录法大大后台处理）。", "撤销合同", {
        confirmButtonText: "确定标记为已撤销",
        cancelButtonText: "取消",
        inputPlaceholder: "如：交易对方信息有误，重新发起"
      }).then(({value}) => {
        cancelContract(row.contractNo, value || "").then(val => {
          if (val && val.code === 200) {
            this.$message.success("已标记为「已撤销」（如需真正作废请在法大大后台处理）");
            this.getData();
          } else {
            this.$message.error((val && val.message) || "撤销失败");
          }
        });
      }).catch(() => {});
    },
    handleMore(cmd, row) {
      if (cmd === "preview") this.openUrl(row.previewUrl);
      else if (cmd === "copyLink") this.copyLinkWithScript(row);
      else if (cmd === "regen") this.doRegen(row);
      else if (cmd === "resend") this.doResend(row);
      else if (cmd === "download") this.doDownload(row.contractNo);
      else if (cmd === "verify") this.doVerify(row);
      else if (cmd === "urge") this.doUrge(row);
      else if (cmd === "beforeAuth") this.doBeforeAuth(row);
      else if (cmd === "recheck") this.doRecheck(row);
      else if (cmd === "restart") this.doRestart(row);
      else if (cmd === "cancel") this.doCancel(row);
    },
    /**
     * 强制重新生成甲方链接。
     * ⚠️ 后端会按"三态"决策返回对外主链接：未实名 ⇒ 实名短链（实名完自动跳签署页）；
     *    已实名 ⇒ 签署短链。前端只展示返回的 signUrl，不要再自己拼。
     * 旧链接会立即作废（换了新交易号），因此必须把新链接重新发给甲方。
     */
    doRegen(row) {
      this.$confirm("将用全新交易号重新生成链接，【旧链接立即作废】，确定继续吗？", "强制重新生成链接", {
        type: "warning",
        confirmButtonText: "重新生成"
      }).then(() => {
        const ld = this.$loading({text: "正在向法大大重新取链接…"});
        regenSignUrl(row.contractNo).then(val => {
          ld.close();
          if (!val || val.code !== 200) {
            this.$message.error((val && val.message) || "重新生成失败");
            return;
          }
          const d = val.data || {};
          this.copyText(d.signUrl || "");
          this.$alert(d.linkType === "verify"
                  ? "交易对方尚未实名，已生成【实名短链】并复制到剪贴板（对方完成实名后会自动跳到签署页）。"
                  : "已生成【签署短链】并复制到剪贴板，请重新发给交易对方。",
              "新链接已生成", {confirmButtonText: "知道了"});
          this.getData();
        }).catch(() => {
          ld.close();
          this.$message.error("重新生成失败");
        });
      }).catch(() => {});
    },
    /** 取交易对方签署意愿认证链接（人脸/短信），属证据链增强，可选 */
    doBeforeAuth(row) {
      const ld = this.$loading({text: "正在取意愿认证链接…"});
      beforeAuthSignUrl(row.contractNo).then(val => {
        ld.close();
        if (!val || val.code !== 200) {
          this.$message.error((val && val.message) || "取意愿认证链接失败");
          return;
        }
        const d = val.data || {};
        this.copyText(d.url || "");
        this.$alert("意愿认证链接已复制到剪贴板，可发给交易对方做人脸/短信意愿确认（可选增强，不签不影响合同效力）。\n"
                + (d.tip || ""), "意愿认证链接", {confirmButtonText: "知道了"});
      }).catch(() => {
        ld.close();
        this.$message.error("取意愿认证链接失败");
      });
    },
    /** 合同验签：校验已签 PDF 签章真伪，归档举证用 */
    doVerify(row) {
      const ld = this.$loading({text: "正在向法大大验签…"});
      verifyContract(row.contractNo).then(val => {
        ld.close();
        if (!val || val.code !== 200) {
          this.$message.error((val && val.message) || "验签失败");
          return;
        }
        const d = val.data || {};
        const raw = d.raw || {};
        this.$alert("验签结论：" + (raw.msg || raw.result || "法大大未返回明确结论")
                + "\n合同号：" + (d.contractNo || row.contractNo),
            "合同验签", {confirmButtonText: "知道了"});
        if (this.detailDrawer) {
          this.openDetail({contractNo: row.contractNo});
        }
      }).catch(() => {
        ld.close();
        this.$message.error("验签失败");
      });
    },
    /** 强制重算状态：本地状态疑似不对时，按法大大权威查询逐方重查并落库纠正 */
    doRecheck(row) {
      const ld = this.$loading({text: "正在按权威查询重算…"});
      recheckContract(row.contractNo).then(val => {
        ld.close();
        if (!val || val.code !== 200) {
          this.$message.error((val && val.message) || "重算失败");
          return;
        }
        const d = val.data || {};
        const diag = d.diag || {};
        const fmt = (p) => {
          const o = p || {};
          return (o.signed ? "已签" : (o.rejected ? "已拒签" : "未签"))
              + (o.notRealNamed ? "·未实名" : "")
              + (o.desc ? "（" + o.desc + "）" : "");
        };
        this.$alert("状态：" + (diag.localStateNameBefore || "-") + " → " + (d.stateName || "-")
                + "\n交易对方：" + fmt(diag.partyA)
                + "\n我方（焱火）：" + fmt(diag.partyB),
            "权威查询重算结果", {confirmButtonText: "知道了"});
        this.getData();
        if (this.detailDrawer) {
          this.openDetail({contractNo: row.contractNo});
        }
      }).catch(() => {
        ld.close();
        this.$message.error("重算失败");
      });
    },
    // ---------- 工具 ----------
    copyText(text) {
      if (!text) return;
      const done = () => this.$message.success("已复制到剪贴板");
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {
        this.$message.warning("复制失败，请手动选择复制");
      }
      document.body.removeChild(ta);
    },
    openUrl(url) {
      if (!url) return;
      window.open(url, "_blank");
    }
  },
  created() {
    this.getTemplates();
    this.getData();
    // 默认开启自动刷新，业务员不用一直手点「刷新」等甲方签
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  unmounted() {
    this.stopAutoRefresh();
  }
}
</script>

<style scoped>
/* 总览统计 */
.stat-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.stat-card {
  flex: 1;
  min-width: 160px;
  padding: 14px 18px;
  border-radius: 8px;
  background: #f4f4f5;
  border-left: 4px solid #c0c4cc;
}

.stat-pending {
  background: #fdf6ec;
  border-left-color: #e6a23c;
}

.stat-done {
  background: #f0f9eb;
  border-left-color: #67c23a;
}

.stat-failed {
  background: #fef0f0;
  border-left-color: #f56c6c;
}

.stat-rejected {
  background: #fef0f0;
  border-left-color: #b91c1c;
}

.stat-num {
  font-size: 26px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}

.stat-label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.stat-tools {
  display: flex;
  align-items: center;
  padding-left: 6px;
}

.auto-tip {
  font-size: 12px;
  color: #909399;
  margin: 0 6px;
}

/* 表单内联提示 */
.field-tip {
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}

.tip-ok {
  color: #67c23a;
}

.tip-warn {
  color: #e6a23c;
}

.tip-info {
  color: #909399;
}

/* ⑤ 证件/银行卡图片缩略图 */
.attach-thumb {
  display: block;
  max-width: 100%;
  max-height: 120px;
  margin-top: 6px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  cursor: pointer;
}

/* ③ 区自动带出折叠条（可点击展开/收起） */
.auto-toggle {
  cursor: pointer;
  margin: 2px 0 10px;
  user-select: none;
}
.auto-toggle:hover {
  opacity: 0.85;
}


.op-strong {
  color: #f56c6c;
  font-weight: 600;
}

.filter-bar {
  padding-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.pagination {
  padding-top: 20px;
  text-align: right;
}

.tip-empty {
  padding: 12px 0;
  color: #909399;
  font-size: 13px;
}

/* 发起结果 */
.result-block {
  margin-top: 16px;
}

.result-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #ebeef5;
}

.result-label {
  width: 110px;
  color: #909399;
  font-size: 13px;
  flex-shrink: 0;
}

.result-value {
  flex: 1;
  word-break: break-all;
  font-size: 13px;
}

.result-value.link {
  color: #a855f7;
}

.result-actions {
  padding-top: 16px;
}

/* 详情 */
.detail-wrap {
  padding: 0 8px 24px;
}

.detail-actions {
  padding-top: 8px;
}

.record-item {
  font-size: 13px;
}

.record-action {
  font-weight: 600;
  color: #303133;
}

.record-meta {
  margin-left: 10px;
  color: #909399;
}

.record-meta span {
  margin-right: 10px;
}

.record-detail {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}
</style>
